/**
 * Button primitive — restyled for the Abyss visual identity (Phase 2).
 *
 * Variants:
 *   - `default`  — frosted glass chrome (bg-bg-elevated + surface ring)
 *   - `primary`  — coral accent (bg-accent, hover/active tokens)
 *   - `ghost`    — borderless, transparent fill
 *   - `danger`   — red destructive action (bg-bad)
 *
 * Sizes: `sm` | `md` (unchanged from Phase 1)
 * Disabled state: 40% opacity, no pointer events (unchanged)
 *
 * No API change — all existing `variant` / `size` / `type` / `ref` usage
 * keeps compiling without modification.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

export type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  default:
    'bg-bg-elevated text-text-1 ring-1 ring-surface-stroke ' +
    'hover:bg-surface-glass hover:ring-accent/40 ' +
    'focus-visible:ring-accent',

  primary:
    'bg-accent text-white ' +
    'hover:bg-accent-hover active:bg-accent-active ' +
    'focus-visible:ring-accent',

  ghost:
    'bg-transparent text-text-2 border border-transparent ' +
    'hover:bg-surface-glass hover:text-text-1 ' +
    'focus-visible:ring-accent',

  danger:
    'bg-bad text-white ' +
    'hover:brightness-110 active:brightness-90 ' +
    'focus-visible:ring-bad',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-xs rounded-md',
  md: 'px-3.5 py-2 text-sm rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'md', type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(
        'inline-flex items-center justify-center gap-1 font-medium',
        'transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
        'disabled:opacity-40 disabled:pointer-events-none',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...props}
    />
  );
});
